import React, { useState } from 'react';
import Navbar from '@/components/zera/Navbar';
import PageHero from '@/components/zera/PageHero';
import Footer from '@/components/zera/Footer';
import ZeraPartners from '@/components/zera/ZeraPartners';
import { notifyForm } from '@/lib/notifyForm';
import { Building2, Users, Award, Globe, ArrowRight, CheckCircle2, Send } from 'lucide-react';


const services = [
  {
    icon: Building2,
    title: 'Executive Retreats',
    description: 'Transform your leadership meetings into inspiring experiences. Our curated retreat venues combine world-class facilities with Albania\'s most breathtaking settings.',
  },
  {
    icon: Users,
    title: 'Team Building',
    description: 'From Riviera sailing regattas to Albanian Alps expeditions, we design team experiences that forge lasting bonds and unlock creative potential.',
  },
  {
    icon: Award,
    title: 'Incentive Travel',
    description: 'Reward your top performers with unforgettable Albanian experiences: private yacht cruises, exclusive wine tastings, and luxury villa retreats.',

  },
  {
    icon: Globe,
    title: 'Conference & Events',
    description: 'Full-service event planning in Albania\'s most prestigious venues, from historic castles to contemporary beachfront resorts.',
  },
];

const CorporateTravel: React.FC = () => {
  const [formData, setFormData] = useState({ company: '', name: '', email: '', phone: '', attendees: '', type: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Add to CRM + email journey@zeratravel.com (non-blocking)
    const [firstName, ...lastParts] = (formData.name || '').trim().split(/\s+/);
    notifyForm({
      email: formData.email,
      firstName: firstName || formData.name,
      lastName: lastParts.join(' '),
      phone: formData.phone,
      source: 'corporate-inquiry',
      subject: `Zera Travel — Corporate Inquiry from ${formData.company || formData.name}`,
      details: {
        company: formData.company,
        contact_name: formData.name,
        attendees: formData.attendees,
        event_type: formData.type,
        message: formData.message,
      },
    });
    setSubmitted(true);
  };


  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <Navbar />

      <PageHero
        image="https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775237785178_e4fb9565.png"
        title={"Corporate\nTravel"}
        subtitle="Elevate your business events with Albania's extraordinary settings, where productivity meets inspiration."

        badge="Business & Events"
        height="h-screen"
        imageAlt="Executive corporate retreat venue in Albania: luxury conference, team-building and incentive travel on the Albanian Riviera"

      />

      {/* Services */}
      <section className="py-20 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Our Services</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#1A3B5C] font-light mt-4">Business Elevated</h2>
            <p className="font-sans text-sm text-[#666] max-w-lg mx-auto mt-4 leading-relaxed">
              Albania offers a unique proposition for corporate travel — exceptional value, stunning venues, and an experience your team will never forget.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service) => (
              <div key={service.title} className="bg-white p-10 border border-[#E8DFD0] hover:border-[#C9A961]/30 transition-all duration-500 group">
                {React.createElement(service.icon, { className: 'w-8 h-8 text-[#C9A961] mb-6 group-hover:scale-110 transition-transform' })}
                <h3 className="font-serif text-xl text-[#1A3B5C] font-light mb-3">{service.title}</h3>
                <p className="font-sans text-sm text-[#666] leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Albania for Business */}
      <section className="py-20 lg:py-32 bg-[#0D1B2A]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">The Albania Advantage</span>
              <h2 className="font-serif text-3xl md:text-4xl text-white font-light mt-4 mb-8">Why Albania for Corporate Events?</h2>
              <div className="space-y-4">
                {[
                  '40-60% cost savings compared to Western European destinations',
                  'Direct flights from major European hubs',
                  'Unique venues unavailable anywhere else in Europe',
                  'Growing luxury hotel infrastructure with international standards',
                  'Mediterranean climate with 300+ days of sunshine',
                  'Exceptional local cuisine for memorable dining experiences',
                  'Safe, welcoming environment with English widely spoken',
                  'Dedicated Zera corporate concierge from planning to execution',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#C9A961] mt-0.5 flex-shrink-0" />
                    <span className="font-sans text-sm text-white/70">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775237781838_4b3e7e21.png"
                alt="Luxury corporate venue in Albania for executive retreats, conferences and team-building events on the Mediterranean coast"
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="py-20 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Get Started</span>
              <h2 className="font-serif text-3xl md:text-4xl text-[#1A3B5C] font-light mt-4">Corporate Inquiry</h2>
            </div>

            {submitted ? (
              <div className="text-center py-16 bg-white border border-[#E8DFD0]">
                <Send className="w-10 h-10 text-[#C9A961] mx-auto mb-4" />
                <h3 className="font-serif text-2xl text-[#1A3B5C] mb-2">Thank You</h3>
                <p className="font-sans text-sm text-[#666]">Our corporate team will be in touch within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6 bg-white p-10 border border-[#E8DFD0]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Company Name</label>
                    <input type="text" required value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent" />
                  </div>
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Contact Name</label>
                    <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Email</label>
                    <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent" />
                  </div>
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Phone</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Number of Attendees</label>
                    <select required value={formData.attendees} onChange={(e) => setFormData({ ...formData, attendees: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent">
                      <option value="">Select</option>
                      <option value="5-15">5 - 15</option>
                      <option value="15-30">15 - 30</option>
                      <option value="30-50">30 - 50</option>
                      <option value="50-100">50 - 100</option>
                      <option value="100+">100+</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Event Type</label>
                    <select required value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent">
                      <option value="">Select</option>
                      <option value="retreat">Executive Retreat</option>
                      <option value="team-building">Team Building</option>
                      <option value="incentive">Incentive Travel</option>
                      <option value="conference">Conference</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999] block mb-2">Tell Us About Your Vision</label>
                  <textarea rows={4} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="w-full border-b border-[#E8DFD0] py-3 font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent resize-none" />
                </div>
                <button type="submit" className="w-full font-sans text-[11px] tracking-[0.25em] uppercase py-4 bg-[#1A3B5C] text-white hover:bg-[#2a5580] transition-all flex items-center justify-center gap-2">
                  Submit Inquiry <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <ZeraPartners />
      <Footer />
    </div>
  );
};

export default CorporateTravel;
